import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useParams } from '@/lib/navigation';
import Link from '@/components/compat/Link';
import { ArrowLeft } from 'lucide-react';
import Navbar from '@/components/help-center/Navbar';
import Footer from '@/components/help-center/Footer';
import { PowerDetailPageSkeleton } from '@/components/help-center/PublicPageSkeleton';
import { PowerSectionSmileyIcon } from '@/components/help-center/powers/power-gallery-view';
import PowerDetailHero from '@/components/help-center/powers/PowerDetailHero';
import PowerDetailOverview from '@/components/help-center/powers/PowerDetailOverview';
import PowerStickersSection from '@/components/help-center/powers/PowerStickersSection';
import PowerSmiliesPawnsSection from '@/components/help-center/powers/PowerSmiliesPawnsSection';
import PowerDetailTutorial from '@/components/help-center/powers/PowerDetailTutorial';
import PowerPstyleSection from '@/components/help-center/powers/PowerPstyleSection';
import PowerEverypowerPstyleSection from '@/components/help-center/powers/PowerEverypowerPstyleSection';
import PowerAvatarFramesSection from '@/components/help-center/powers/PowerAvatarFramesSection';
import PowerEverypowerSection from '@/components/help-center/powers/PowerEverypowerSection';
import PowerAllpowersSection from '@/components/help-center/powers/PowerAllpowersSection';
import PowerCollectionSection from '@/components/help-center/powers/PowerCollectionSection';
import PowerDetailRelated from '@/components/help-center/powers/PowerDetailRelated';
import PowerDocSectionsAt from '@/components/help-center/powers/PowerDocSectionsAt';
import PowerDetailLoreSection from '@/components/help-center/powers/PowerDetailLoreSection';
import PowerBotProvidersSection from '@/components/help-center/powers/PowerBotProvidersSection';
import { HeroBackdropLayers } from '@/components/help-center/latest-power-shared';
import { resolvePowerForPublicDetail, type PowerInfo } from '@/lib/powers-data';
import { usePowersCms, usePowerWithFairtradePrices } from '@/components/painel/powers/powers-cms-context';
import * as powersService from '@/services/powers.service';
import { analytics } from '@/services/analytics';
import { CmsPublicEditButton } from '@/components/help-center/CmsPublicEditButton';

export default function PowerDetailPage() {
  const { power: slug = '' } = useParams<{ power: string }>();
  const { getPowerBySlug: getCmsPower, hydrated: cmsHydrated } = usePowersCms();

  // Nunca semear mock/CMS antigo: evita flash ILIMITADO → LIMITADO (ex.: bowgal).
  const [power, setPower] = useState<PowerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!slug) return;

    let cancelled = false;
    setLoading(true);
    setError(false);

    const load = async () => {
      const cmsFromApi = await powersService.getPublicPower(slug).catch(() => null);
      const cmsLocal = getCmsPower(slug);
      const preferLocal =
        cmsLocal &&
        cmsFromApi &&
        (Date.parse(cmsLocal.updatedAt) || 0) >= (Date.parse(cmsFromApi.updatedAt) || 0);

      const resolved = await resolvePowerForPublicDetail(slug, {
        cmsHydrated: cmsHydrated || Boolean(cmsFromApi) || Boolean(cmsLocal),
        getCmsPower,
        cmsPower: preferLocal ? cmsLocal : cmsFromApi ?? cmsLocal,
        requirePublicCms: true,
        excludeAce: true,
      });

      if (cancelled) return;

      if (!resolved) {
        setPower(null);
        setError(true);
      } else {
        setPower(resolved);
        setError(false);
      }
      setLoading(false);
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [slug, cmsHydrated, getCmsPower]);

  const displayPower = usePowerWithFairtradePrices(power);

  useEffect(() => {
    if (!displayPower) return;
    void analytics.powerViewed({
      powerId: displayPower.id,
      slug: displayPower.slug ?? slug,
      action: 'view',
    });
  }, [displayPower?.id, displayPower?.slug, slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030023] flex flex-col">
        <Navbar />
        <main className="flex-1">
          <PowerDetailPageSkeleton />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !displayPower) {
    return (
      <div className="min-h-screen bg-[#030023] flex flex-col">
        <Navbar />
        <div className="relative flex-1 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0" aria-hidden>
            <HeroBackdropLayers className="absolute inset-0" />
          </div>
          <div className="relative z-10 flex flex-col items-center gap-6 text-center px-6 py-24">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/10 border border-red-500/20">
              <PowerSectionSmileyIcon code="sherlock" size="xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-poppins mb-2">Power não encontrado</h1>
              <p className="text-white/50 font-inter max-w-md">
                O power <strong className="text-white">&ldquo;{slug}&rdquo;</strong> não foi encontrado.
              </p>
            </div>
            <Link
              href="/powers"
              className="flex items-center gap-2 rounded-xl bg-sky-400/15 border border-sky-400/25 px-5 py-2.5 text-sm font-semibold text-sky-300 font-poppins hover:bg-sky-400/25 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Ver todos os powers
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <PowerDetailHero
          power={displayPower}
          editAction={
            <CmsPublicEditButton
              href={`/painel/powers/${displayPower.slug}/edit`}
              permission={['powers.edit', 'powers.review']}
              label="Editar power"
              variant="hero"
            />
          }
        />
        <PowerDetailOverview power={displayPower} />
        <PowerDetailLoreSection power={displayPower} />
        <PowerDocSectionsAt power={displayPower} position="after-overview" />
        {displayPower.slug === 'bot' ? <PowerBotProvidersSection /> : null}
        <PowerAllpowersSection power={displayPower} />
        <PowerEverypowerSection power={displayPower} />
        <PowerCollectionSection power={displayPower} />
        <PowerDocSectionsAt power={displayPower} position="after-special" />
        <PowerEverypowerPstyleSection power={displayPower} />
        <PowerPstyleSection power={displayPower} />
        <PowerDocSectionsAt power={displayPower} position="after-pstyle" />
        <PowerAvatarFramesSection power={displayPower} />
        <PowerDocSectionsAt power={displayPower} position="after-avatar-frames" />
        <PowerStickersSection power={displayPower} />
        <PowerSmiliesPawnsSection power={displayPower} />
        <PowerDocSectionsAt power={displayPower} position="after-smilies" />
        <PowerDocSectionsAt power={displayPower} position="before-tutorial" />
        <PowerDetailTutorial power={displayPower} />
        <PowerDocSectionsAt power={displayPower} position="after-tutorial" />
        <PowerDocSectionsAt power={displayPower} position="before-related" />
        <PowerDetailRelated power={displayPower} />
      </main>
      <Footer />
    </div>
  );
}
